import { useMutation, useQuery } from "@tanstack/react-query";
import { endpoints, fetcher, poster } from "../utils/axios";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { useExam } from "../context/ExamContext";
import { useExamStorage } from "../hooks/useExamStorage";
import { getItemFromExamStorage } from "../utils/examStorageHelper";
import { useSaving } from '../context/SavingContext';
import { KEYS } from "../constants/storageKeys";

export const useGetReading = () => {
  const { isRealExam } = useExam();

  const readingQuestions = getItemFromExamStorage(KEYS.READING_QUESTIONS, isRealExam)

  const memoizedValue = useMemo(
    () => ({
      reading: readingQuestions,
      isLoadingReadingTest: false
    }),
    []
  );

  return memoizedValue;
};

export const useGetReadingPassage = (options = {}) => {
  const { stage, isRealExam } = useExam();
  const readingQuestions = getItemFromExamStorage(KEYS.READING_QUESTIONS, isRealExam)

  const readingPassageData = (() => {
    try {
      if (readingQuestions[`passage${stage}`]) {
        return readingQuestions[`passage${stage}`];
      } else {
        console.log(`Passage${stage} does not exist in the data`);
        return null;
      }
    } catch (error) {
      console.error("Error parsing readingQuestions:", error);
      return null;
    }
  })();

  const memoizedValue = useMemo(
    () => ({
      readingPassage: readingPassageData,
      isLoadingReadingTest: false
    }),
    [readingPassageData]
  );

  return memoizedValue;
};

//-------------------------------------------------------------------------------------------

export const useGetListening = () => {
  const { isRealExam } = useExam();

  const listeningQuestions = getItemFromExamStorage(KEYS.LISTENING_QUESTIONS, isRealExam)

  const memoizedValue = useMemo(
    () => ({
      listening: listeningQuestions,
      isLoadingReadingTest: false
    }),
    []
  );

  return memoizedValue;
};

export const useGetListeningPart = () => {
  const { stage, isRealExam } = useExam();

  const listeningQuestions = getItemFromExamStorage(KEYS.LISTENING_QUESTIONS, isRealExam)

  const listeningPartData = (() => {
    try {
      if (listeningQuestions[`part${stage}`]) {
        return listeningQuestions[`part${stage}`];
      } else {
        console.log(`Part ${stage} does not exist in the data`);
        return null;
      }
    } catch (error) {
      console.error("Error parsing readingQuestions:", error);
      return null;
    }
  })();

  const memoizedValue = useMemo(
    () => ({
      listeningPart: listeningPartData,
      isLoadingListeningTest: false,
    }),
    [listeningPartData]
  );

  return memoizedValue;
};

//-------------------------------------------------------------------------------------------

export const useGetWritingTest = () => {
  const { isRealExam } = useExam();
  const writingQuestions = getItemFromExamStorage(KEYS.WRITING_QUESTIONS, isRealExam)

  const memoizedValue = useMemo(
    () => ({
      writingTest: writingQuestions,
    }),
    []
  );

  return memoizedValue;
};

//-------------------------------------------------------------------------------------------

export const useGetExamRegistrationDetail = () => {
  const URL = endpoints.exam.getExamRegistrationDetail();
  const { data, isPending, error } = useQuery({
    queryKey: ["examRegistrationDetail"],
    queryFn: async () => {
      const res = await fetcher(URL);
      return res.data;
    },
    refetchOnWindowFocus: false,
  });

  return useMemo(() => ({
    examRegistrationDetails: data,
    isLoadingRegistrationDetails: isPending,
    errorRegistrationDetails: error,
  }), [data, isPending, error]); // Only update when values change
};


//------------------------------------------------------------------------------------------

export const useGetExam = (isEnabled = false, productId) => {
  const { stage, setExamType } = useExam();

  const URL = endpoints.getExam(productId);

  const { data, isPending, isSuccess, error } = useQuery({
    queryKey: ["exam"],
    queryFn: async () => {
      const res = await fetcher(URL);
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  return useMemo(() => ({
    exam: data,
    isLoadingExam: isPending && isEnabled,
    isSuccessExam: isSuccess,
    errorExam: error,
  }), [data, isPending, error]);
};

//------------------------------------------------------------------------------------------

export const useGetOngoingExam = (isEnabled = false, productId) => {
  const URL = `${process.env.REACT_APP_API_URL}/exam/getOngoingExamWithDetails/${productId}`;

  const { data, isPending, error } = useQuery({
    queryKey: ["ongoingExam"],
    queryFn: async () => {
      const res = await fetcher(URL);
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  return useMemo(() => ({
    ongoingExam: data,
    isLoadingOngoingExam: isPending && isEnabled,
    errorOngoingExam: error,
  }), [data, isPending, error]);
};

//------------------------------------------------------------------------------------------

export const useGetPracticeTest = (isEnabled = false, productId) => {
  const URL = `${process.env.REACT_APP_API_URL}/exam/getPracticeExamWithDetails/${productId}`;
  const queryKey = ['practiceTest', productId]

  const { data, isPending, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await fetcher(URL);
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  return useMemo(() => ({
    practiceExamData: data,
    isLoadingPracticeExamData: isPending && isEnabled,
    errorPracticeExamData: error,
  }), [data, isPending, error]);
};

//------------------------------------------------------------------------------------------

export const useGetSectionPracticeTest = (isEnabled = false, productType, productId) => {
  const URL = `${process.env.REACT_APP_API_URL}/${productType}/getPracticeTestById/${productId}`;
  const queryKey = ['sectionPracticeTest', productType, productId]
  const { data, isPending, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await fetcher(URL);
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  return useMemo(() => ({
    sectionPracticeTestData: data,
    isLoadingSectionPracticeTest: isPending && isEnabled,
    errorSectionPracticeTest: error,
  }), [data, isPending, error]);
};

//------------------------------------------------------------------------------------------


export const useGetUserAnswers = (productType, userProductRegistrationId) => {
  const url = endpoints.getUserAnswers(productType + "UserAnswers", userProductRegistrationId);

  const { mutate, data, isPending, error } = useMutation({
    mutationKey: ['getUserAnswers', productType, userProductRegistrationId],
    mutationFn: async () => {
      const res = await fetcher(url);
      return res.data;
    },
    onError: () => toast.error(error),
  });



  return {
    triggerGetUserAnswers: mutate,
    userAnswers: data,
    isLoadingUserAnswers: isPending,
    errorUserAnswers: error,
  };
};

//------------------------------------------------------------------------------------------


export const useCreateInstantExamSchedule = (userProductRegistrationId) => {
  const url = endpoints.createInstantExamSchedule(userProductRegistrationId);

  const { mutate, data, isPending, error } = useMutation({
    mutationKey: ['createInstantExamSchedule', userProductRegistrationId],
    mutationFn: async () => {
      const res = await poster(url);
      return res.data;
    },
    onError: () => toast.error(error),
  });

  return {
    triggerCreateInstantExamSchedule: mutate,
    createdInstantExamSchedule: data,
    isLoadingCreateInstantExamSchedule: isPending,
    errorCreateInstantExamSchedule: error,
  };
}



//------------------------------------------------------------------------------------------
export const useGetSessionDetails = (isEnabled = false, userProductRegistrationId) => {
  const URL = endpoints.sessionDetails(userProductRegistrationId);
  const queryKey = ['sessionDetails', userProductRegistrationId]

  const { data, isPending, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await fetcher(URL);
      return res.data;
    },
    refetchOnWindowFocus: false,
    enabled: isEnabled
  });

  return useMemo(() => ({
    sessionDetails: data,
    isLoadingSessionDetails: isEnabled ? isPending : false,
    errorSessionDetails: error,
  }), [data, isPending, error]);
};

// ------------------------------------------------------------------------------------------

export const useUpdateUserReadingAnswers = () => {
  const url = endpoints.readingUserAnswersUpdateBulk;
  const { setIsSaving } = useSaving();


  const { data, mutate, isPending, error } = useMutation({
    mutationKey: ["updateBulk"],
    mutationFn: async (payload) => {
      setIsSaving(true);
      return await poster(url, payload);
    },
    onSuccess: () => {
      setIsSaving(false);
    },
    onError: () => {
      setIsSaving(false);
      toast.error('2Saving Failed!');
    },
  });

  const memoizedValue = useMemo(
    () => ({
      updateBulk: mutate,
      updateBulkData: data,
      isLoadingUpdateBulk: isPending,
      errorUpdateBulk: error,
    }),
    [data, error, isPending, mutate]
  );

  return memoizedValue;
};


// ------------------------------------------------------------------------------------------

export const useUpdateUserListeningAnswers = () => {
  const url = endpoints.listeningUserAnswersUpdateBulk;
  const { setIsSaving } = useSaving();

  const { data, mutate, isPending, error } = useMutation({
    mutationKey: ["updateBulk"],
    mutationFn: async (payload) => {
      setIsSaving(true);
      return await poster(url, payload);
    },
    onSuccess: () => {
      setIsSaving(false);
    },
    onError: () => {
      setIsSaving(false);
      toast.error('3Saving Failed!');
    },
  });

  const memoizedValue = useMemo(
    () => ({
      updateBulk: mutate,
      updateBulkData: data,
      isLoadingUpdateBulk: isPending,
      errorUpdateBulk: error,
    }),
    [data, error, isPending, mutate]
  );

  return memoizedValue;
};


// ------------------------------------------------------------------------------------------

export const useUpdateUserWritingAnswers = () => {
  const url = endpoints.writingUserAnswersUpdateBulk;
  const { setIsSaving } = useSaving();

  const { data, mutate, isPending, error } = useMutation({
    mutationKey: ["updateBulk"],
    mutationFn: async (payload) => {
      setIsSaving(true);
      return await poster(url, payload);
    },
    onSuccess: () => {
      setIsSaving(false);
    },
    onError: () => {
      setIsSaving(false);
      toast.error('4Saving Failed!');
    },
  });

  const memoizedValue = useMemo(
    () => ({
      updateBulk: mutate,
      updateBulkData: data,
      isLoadingUpdateBulk: isPending,
      errorUpdateBulk: error,
    }),
    [data, error, isPending, mutate]
  );

  return memoizedValue;
};


// ------------------------------------------------------------------------------------------


export const useFinishSession = () => {
  const { setIsSaving } = useSaving();

  const { mutate, isPending, error, data } = useMutation({
    mutationKey: ["finishSession"],
    mutationFn: async () => {
      const sid = sessionStorage.getItem(KEYS.PS_SESSION_ID);
      const tok = sessionStorage.getItem(KEYS.PS_SESSION_TOKEN);

      if (!sid || !tok) {
        console.warn("Session info missing. Finishing skipped.");
        setIsSaving(false);
        return { skipped: true };
      }

      setIsSaving(true);

      const url = `${process.env.REACT_APP_API_URL}/sessions/${encodeURIComponent(sid)}/finish`;
      const payload = JSON.stringify({ sessionToken: tok });

      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: "application/json" });
          const ok = navigator.sendBeacon(url, blob);
          if (!ok) throw new Error("sendBeacon failed");
          return { ok: true, method: "beacon" };
        } else {
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: payload,
            keepalive: true,
            credentials: "include",
          });
          if (!res.ok) throw new Error("Fetch failed");
          return await res.json();
        }
      } finally {
        try {
          sessionStorage.removeItem(KEYS.PS_SESSION_ID);
          sessionStorage.removeItem(KEYS.PS_SESSION_TOKEN);
        } catch (e) {
          console.warn("Session cleanup failed", e);
        }
      }
    },
    onSuccess: () => {
      setIsSaving(false);
    },
    onError: (err) => {
      setIsSaving(false);
      if (err.message !== "Missing session ID or token") {
        toast.error("Saving Failed!");
      } else {
        console.warn("Skipping toast: session missing");
      }
    },
  });

  // Wrap mutate to conditionally call it
  const triggerFinishSession = () => {
    const sid = sessionStorage.getItem(KEYS.PS_SESSION_ID);
    const tok = sessionStorage.getItem(KEYS.PS_SESSION_TOKEN);

    if (!sid || !tok) {
      console.warn("Session ID or token missing — not triggering finish mutation");
      return;
    }

    mutate();
  };

  return {
    triggerFinishSession,
    isLoadingFinishSession: isPending,
    errorFinishSession: error,
    finishSessionData: data,
  };
};


// ------------------------------------------------------------------------------------------

export const useCheckSectionPracticeResult = (productType, userProductRegistrationId) => {
  const url = endpoints.checkSectionPracticeResult(productType, userProductRegistrationId);
  const { setIsSaving } = useSaving();

  const { mutate, data, isPending, error } = useMutation({
    mutationKey: ['checkResult', productType, userProductRegistrationId],
    mutationFn: async (payload) => {
      setIsSaving(true);
      return await poster(url, payload);
    },
    onSuccess: () => {
      setIsSaving(false);
    },
    onError: () => {
      setIsSaving(false);
      toast.error('6Saving Failed!');
    },
  });

  return {
    triggerCheckSectionResult: mutate,
    data,
    isLoadingCheckResult: isPending,
    errorCheckResult: error,
  };
};



// ------------------------------------------------------------------------------------------

export const useCheckInstantResult = (userProductRegistrationId) => {
  const url = endpoints.checkInstantResult(userProductRegistrationId);
  const { setIsSaving } = useSaving();

  const { mutate, data, isPending, error } = useMutation({
    mutationKey: ['checkInstantResult', userProductRegistrationId],
    mutationFn: async (payload) => {
      setIsSaving(true);
      return await poster(url, payload);
    },
    onSuccess: () => {
      setIsSaving(false);
    },
    onError: (err) => {
      setIsSaving(false);
      if (err.message !== "Missing session ID or token") {
        toast.error("Saving Failed!");
      } else {
        console.warn("Skipping toast: session missing");
      }
    },
  });

  return {
    triggerCheckInstantResult: mutate,
    data,
    isLoadingCheckPracticeResult: isPending,
    errorCheckPracticeResult: error,
  };
};


